/**
 * ebookalia — Cinematic scroll layer (GSAP + ScrollTrigger).
 *
 * Owns every entrance, parallax and depth effect on the course landing when
 * GSAP is available. If the library fails to load, this file exits without
 * touching the DOM and the CSS fallback in course-landing.css takes over, so
 * sales copy is never left invisible.
 *
 * Safety design: the first thing the animation pass does is force every
 * `.cl-reveal` element to its final visible state. Every effect is then built
 * with `.from()`, which animates *out of* a hidden state that GSAP itself
 * controls. Anything no animation claims simply stays visible — there is no
 * code path that can leave content stuck at opacity 0.
 *
 * Purely presentational. It never touches the product form, cart, checkout,
 * customer accounts or Tevello.
 */
(function () {
  'use strict';

  if (window.__ebookaliaCinematic) return;

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;

  // No GSAP -> leave `cl-anim` alone and let the CSS reveal handle things.
  if (!gsap || !ScrollTrigger) return;

  window.__ebookaliaCinematic = true;

  try {
    gsap.registerPlugin(ScrollTrigger);
  } catch (error) {
    return;
  }

  var root = document.documentElement;

  function boot() {
    if (!document.querySelector('.cl')) return;

    root.classList.add('cl-gsap');

    // Hand every revealable element to GSAP in its final state.
    gsap.set('.cl-reveal', { opacity: 1, y: 0, scale: 1, rotationX: 0 });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Visible, in place, no motion whatsoever.
      return;
    }

    // Fewer callbacks, and no re-measure on mobile URL-bar resize.
    ScrollTrigger.config({ limitCallbacks: true, ignoreMobileResize: true });

    var mm = gsap.matchMedia();

    mm.add(
      {
        isDesktop: '(min-width: 750px)',
        isMobile: '(max-width: 749px)'
      },
      function (context) {
        var isDesktop = context.conditions.isDesktop;

        // Motion is scaled down on phones: shorter travel, no 3D tilt and
        // lighter parallax, so scrolling stays smooth on mid-range hardware.
        var rise = isDesktop ? 70 : 38;
        var tilt = isDesktop ? 8 : 0;
        var drift = isDesktop ? 14 : 6;
        var ease = 'power3.out';

        /* ---------------------------------------------------------------
           Hero — staged opening, then a slow parallax exit
           --------------------------------------------------------------- */

        var hero = document.querySelector('.cl-hero');

        if (hero) {
          var q = function (selector) {
            return hero.querySelectorAll(selector);
          };

          // Deliberately transform-only: no tween on this page starts an element
          // at opacity 0. If the ticker ever stalls mid-timeline (hidden tab,
          // starved rAF, throttled device) the worst case is copy that has not
          // finished sliding into place — never copy that is invisible.
          var heroTl = gsap
            .timeline({ defaults: { ease: ease, duration: 1 } })
            .from(q('.cl-eyebrow'), { y: 26, duration: 0.75 })
            .from(q('.cl-hero__title'), { y: rise, scale: 0.965, duration: 1.25 }, '-=0.45')
            .from(q('.cl-hero__lede'), { y: 32 }, '-=0.85')
            .from(q('.cl-hero__cta'), { y: 30, scale: 0.97 }, '-=0.8')
            .from(q('.cl-hero__trust li'), { y: 16, duration: 0.65, stagger: 0.09 }, '-=0.7')
            .from(
              q('.cl-hero__visual'),
              {
                y: 90,
                scale: 0.9,
                rotationX: tilt,
                transformPerspective: 1400,
                transformOrigin: 'center top',
                duration: 1.5,
                ease: 'power2.out'
              },
              '-=1.1'
            );

          // Belt and braces for the transforms: setTimeout is not throttled the
          // way requestAnimationFrame is, so if the ticker stalls the opening
          // still lands in its final position instead of freezing part-way.
          window.setTimeout(function () {
            if (heroTl.progress() < 1) heroTl.progress(1);
          }, 3500);

          // The visual keeps drifting while the hero scrolls away. When a real
          // image is present we move the image *inside* its clipping frame,
          // which reads as true parallax rather than a sliding panel.
          var heroFrame = hero.querySelector('.cl-hero__visual .cl-frame');
          var heroImage = heroFrame && heroFrame.querySelector('img');
          var heroVisualInner = heroImage || hero.querySelector('.cl-hero__visual > *');

          if (heroVisualInner) {
            if (heroImage) {
              // Oversize slightly so there is material to travel behind the crop.
              gsap.set(heroImage, { scale: 1.12, transformOrigin: 'center center' });
              gsap.fromTo(
                heroImage,
                { yPercent: drift * 0.45 },
                {
                  yPercent: -drift * 0.45,
                  ease: 'none',
                  scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.6 }
                }
              );
            } else {
              gsap.to(heroVisualInner, {
                yPercent: -drift,
                scale: 1.05,
                ease: 'none',
                scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: 0.6 }
              });
            }
          }

          // Copy lifts faster than the page scrolls, which is what sells the
          // depth. It must never read as "the content disappeared":
          //  - on phones the hero copy is still on screen while this runs, so
          //    it only drifts and keeps full opacity;
          //  - on desktop it recedes to 0.4, never near zero, and only once the
          //    hero is genuinely on its way out.
          var heroCopy = q('.cl-hero__title, .cl-hero__lede, .cl-hero__cta, .cl-hero__trust');

          if (heroCopy.length) {
            gsap.to(heroCopy, {
              yPercent: isDesktop ? -22 : -8,
              opacity: isDesktop ? 0.4 : 1,
              ease: 'none',
              scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom 10%', scrub: 0.5 }
            });
          }
        }

        /* ---------------------------------------------------------------
           Section headings — rise into place, line by line
           --------------------------------------------------------------- */

        gsap.utils.toArray('.cl-head').forEach(function (head) {
          gsap.from(head.children, {
            y: rise * 0.5,
            duration: 0.95,
            stagger: 0.1,
            ease: ease,
            scrollTrigger: { trigger: head, start: 'top 88%' }
          });
        });

        /* ---------------------------------------------------------------
           Banners — text staggers in, the panel expands open on scroll
           --------------------------------------------------------------- */

        gsap.utils.toArray('.cl-banner').forEach(function (banner) {
          var content = banner.querySelector('.cl-banner__content');
          var media = banner.querySelector('.cl-banner__media');

          if (content) {
            gsap.from(content.children, {
              y: rise * 0.55,
              duration: 0.95,
              stagger: 0.1,
              ease: ease,
              scrollTrigger: { trigger: banner, start: 'top 78%' }
            });
          }

          if (media) {
            // Scrubbed, so it reads as a real expansion tied to the scroll
            // rather than a one-shot fade.
            gsap.from(media, {
              scale: 0.82,
              y: 60,
              rotationX: tilt * 0.6,
              transformPerspective: 1200,
              ease: 'power2.out',
              scrollTrigger: { trigger: banner, start: 'top 88%', end: 'top 32%', scrub: 0.8 }
            });

            // Prefer moving the image inside its clipping frame; fall back to
            // the placeholder panel when no real image is configured.
            var mediaFrame = media.querySelector('.cl-frame');
            var mediaImage = mediaFrame && mediaFrame.querySelector('img');
            var mediaInner = mediaImage || media.firstElementChild;

            if (mediaInner) {
              if (mediaImage) {
                gsap.set(mediaImage, { scale: 1.12, transformOrigin: 'center center' });
              }

              gsap.fromTo(
                mediaInner,
                { yPercent: drift * 0.5 },
                {
                  yPercent: -drift * 0.5,
                  ease: 'none',
                  scrollTrigger: { trigger: banner, start: 'top bottom', end: 'bottom top', scrub: true }
                }
              );
            }
          }
        });

        /* ---------------------------------------------------------------
           Card grids — staggered rise with a touch of depth
           --------------------------------------------------------------- */

        gsap.utils.toArray('.cl-grid').forEach(function (grid) {
          gsap.from(grid.children, {
            y: rise * 0.8,
            scale: 0.94,
            rotationX: tilt * 0.5,
            transformPerspective: 1000,
            duration: 1,
            stagger: 0.08,
            ease: ease,
            scrollTrigger: { trigger: grid, start: 'top 84%' }
          });
        });

        // Oversized module numerals drift against the scroll.
        gsap.utils.toArray('.cl-module__num').forEach(function (numeral) {
          gsap.fromTo(
            numeral,
            { yPercent: -18 },
            {
              yPercent: 24,
              ease: 'none',
              scrollTrigger: {
                trigger: numeral.parentElement,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true
              }
            }
          );
        });

        /* ---------------------------------------------------------------
           Opportunity stats
           --------------------------------------------------------------- */

        gsap.utils.toArray('.cl-stats').forEach(function (row) {
          gsap.from(row.children, {
            y: 48,
            scale: 0.92,
            duration: 0.9,
            stagger: 0.12,
            ease: 'back.out(1.5)',
            scrollTrigger: { trigger: row, start: 'top 86%' }
          });
        });

        /* ---------------------------------------------------------------
           Journey — the rail draws itself, then the steps land in sequence
           --------------------------------------------------------------- */

        var steps = document.querySelector('.cl-journey__steps');

        if (steps) {
          if (isDesktop) {
            gsap.fromTo(
              steps,
              { '--cl-rail': 0 },
              {
                '--cl-rail': 1,
                ease: 'none',
                scrollTrigger: { trigger: steps, start: 'top 82%', end: 'center 58%', scrub: 0.6 }
              }
            );
          }

          gsap.from(steps.children, {
            y: rise,
            scale: 0.93,
            duration: 1,
            stagger: 0.16,
            ease: ease,
            scrollTrigger: { trigger: steps, start: 'top 82%' }
          });
        }

        /* ---------------------------------------------------------------
           Footnotes and the final CTA — the closing beat
           --------------------------------------------------------------- */

        gsap.utils.toArray('.cl-note').forEach(function (note) {
          gsap.from(note, {
            y: 28,
            duration: 0.85,
            ease: ease,
            scrollTrigger: { trigger: note, start: 'top 92%' }
          });
        });

        var finalCta = document.querySelector('.cl-final__inner');

        if (finalCta) {
          gsap.from(finalCta.children, {
            y: rise * 0.7,
            scale: 0.95,
            duration: 1.05,
            stagger: 0.12,
            ease: ease,
            scrollTrigger: { trigger: finalCta, start: 'top 84%' }
          });
        }
      }
    );

    // Images and webfonts change section heights; re-measure once settled.
    window.addEventListener('load', function () {
      ScrollTrigger.refresh();
    });
  }

  function safeBoot() {
    try {
      boot();
    } catch (error) {
      // Never let an animation bug hide sales copy: make everything visible
      // and hand control back to the CSS layer.
      if (window.gsap) gsap.set('.cl-reveal', { clearProps: 'all' });
      root.classList.remove('cl-gsap');
      if (window.console && console.warn) {
        console.warn('[ebookalia] cinematic layer disabled:', error);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeBoot);
  } else {
    safeBoot();
  }

  // Theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });
})();
